import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { CurrencyEur, CaretLeft, CaretRight, X, Funnel } from '@phosphor-icons/react';

const MONTH_NAMES = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

const formatMonth = (m) => {
  if (!m) return '-';
  const [year, month] = m.split('-');
  return `${MONTH_NAMES[parseInt(month) - 1]} ${year}`;
};

const formatCurrency = (v) => `${parseFloat(v || 0).toFixed(2)} \u20AC`;

const formatDate = (d) => {
  if (!d) return '-';
  const parts = d.split('-');
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

export const PaymentsView = ({ onTotalChange, anno }) => {
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [totals, setTotals] = useState({ total_amount: 0, paid_amount: 0, dovuto_insegnante: 0 });
  const [availableMonths, setAvailableMonths] = useState([]);
  const [dataEsportazione, setDataEsportazione] = useState('');

  const [filterMonth, setFilterMonth] = useState('');
  const [filterStudent, setFilterStudent] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const LIMIT = 50;

  const loadPayments = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (anno) params.anno = anno;
      if (filterMonth) params.month = filterMonth;
      if (filterStudent) params.student_name = filterStudent;
      if (filterStatus) params.status = filterStatus;
      const response = await api.getPayments(params);
      const data = response.data;
      setPayments(data.records);
      setTotal(data.total);
      setPages(data.pages);
      setTotals(data.totals);
      if (data.available_months) setAvailableMonths(data.available_months);
      if (data.data_esportazione) setDataEsportazione(data.data_esportazione);
      if (onTotalChange) onTotalChange(data.total);
    } catch (error) {
      console.error('Error loading payments:', error);
    } finally {
      setLoading(false);
    }
  }, [page, filterMonth, filterStudent, filterStatus, onTotalChange, anno]);

  useEffect(() => { loadPayments(); }, [loadPayments]);
  useEffect(() => { setPage(1); }, [filterMonth, filterStudent, filterStatus]);

  const clearFilters = () => { setFilterMonth(''); setFilterStudent(''); setFilterStatus(''); setPage(1); };
  const hasFilters = filterMonth || filterStudent || filterStatus;

  return (
    <div>
      {/* Data aggiornamento */}
      {dataEsportazione && (
        <div className="mb-4 px-4 py-2.5 bg-[#2D2A4A] text-white rounded-sm inline-flex items-center gap-2" data-testid="data-esportazione">
          <span className="text-sm font-semibold tracking-wide">Dati aggiornati al {formatDate(dataEsportazione)}</span>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-white border border-[#E4E4E7] rounded-sm p-4 text-center" data-testid="payments-total-card">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#52525B] mb-1">Totale Addebiti</div>
          <div className="text-2xl font-bold font-['Work_Sans']">{formatCurrency(totals.total_amount)}</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-sm p-4 text-center" data-testid="payments-paid-card">
          <div className="text-xs font-semibold uppercase tracking-wider text-green-700 mb-1">Importo Pagato</div>
          <div className="text-2xl font-bold font-['Work_Sans'] text-green-800">{formatCurrency(totals.paid_amount)}</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 text-center" data-testid="payments-dovuto-card">
          <div className="text-xs font-semibold uppercase tracking-wider text-blue-700 mb-1">Dovuto Insegnante</div>
          <div className="text-2xl font-bold font-['Work_Sans'] text-blue-800">{formatCurrency(totals.dovuto_insegnante)}</div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Funnel size={16} className="text-[#52525B]" />
        <Select value={filterMonth} onValueChange={setFilterMonth}>
          <SelectTrigger className="w-[200px] rounded-sm h-9 text-sm" data-testid="filter-payment-month">
            <SelectValue placeholder="Tutti i periodi" />
          </SelectTrigger>
          <SelectContent>
            {availableMonths.map(m => (
              <SelectItem key={m} value={m}>{formatMonth(m)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input type="text" placeholder="Cerca alunno..." value={filterStudent} onChange={(e) => setFilterStudent(e.target.value)} className="w-[200px] rounded-sm h-9 text-sm uppercase" data-testid="filter-payment-student" />
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[160px] rounded-sm h-9 text-sm" data-testid="filter-payment-status"><SelectValue placeholder="Tutti gli stati" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Pagato">Pagato</SelectItem>
            <SelectItem value="Non Pagato">Non Pagato</SelectItem>
          </SelectContent>
        </Select>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 text-sm text-[#52525B]" data-testid="clear-payment-filters"><X size={14} className="mr-1" />Pulisci</Button>
        )}
        <div className="text-sm text-[#52525B] font-medium ml-auto" data-testid="payments-count">{total} {total === 1 ? 'addebito' : 'addebiti'}</div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-[#52525B]">Caricamento...</div>
      ) : payments.length === 0 ? (
        <div className="text-center py-12 bg-white border border-[#E4E4E7] rounded-sm">
          <CurrencyEur size={48} className="mx-auto text-[#E4E4E7] mb-4" />
          <p className="text-[#52525B]">{hasFilters ? 'Nessun addebito trovato' : 'Nessun addebito registrato'}</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden">
          <table className="w-full" data-testid="payments-table">
            <thead>
              <tr className="border-b border-[#E4E4E7] bg-[#FAFAFA]">
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Periodo</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Alunno</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Descrizione</th>
                <th className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Importo</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Scadenza</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Pagamento</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Stato</th>
                <th className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Dovuto Insegnante</th>
              </tr>
            </thead>
            <tbody>
              {payments.map(payment => (
                <tr key={payment.id} className="border-b border-[#E4E4E7] hover:bg-[#FAFAFA]" data-testid={`payment-row-${payment.id}`}>
                  <td className="px-3 py-1.5 text-sm">{formatMonth(payment.month)}</td>
                  <td className="px-3 py-1.5 font-medium uppercase text-sm">{payment.student_name}</td>
                  <td className="px-3 py-1.5 text-sm text-[#52525B] max-w-[200px] truncate">{payment.descrizione || '-'}</td>
                  <td className="px-3 py-1.5 text-sm text-right font-medium font-['Work_Sans']">{formatCurrency(payment.importo)}</td>
                  <td className="px-3 py-1.5 text-sm text-[#52525B]">{formatDate(payment.data_scadenza)}</td>
                  <td className="px-3 py-1.5 text-sm text-[#52525B]">{formatDate(payment.data_pagamento)}</td>
                  <td className="px-3 py-1.5">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${payment.status === 'Pagato' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-testid={`payment-status-${payment.id}`}>
                      {payment.status}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-sm text-right font-medium font-['Work_Sans']">
                    {payment.status === 'Pagato' ? formatCurrency(payment.importo_insegnante) : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between mt-3 mb-12">
          <div className="text-sm text-[#52525B]">Pagina {page} di {pages}</div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="rounded-sm" data-testid="prev-payment-page">
              <CaretLeft size={14} className="mr-1" />Precedente
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages} className="rounded-sm" data-testid="next-payment-page">
              Successiva<CaretRight size={14} className="ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
